import React, { useEffect, useState } from "react";
import { supabase } from "../../supabase";
import { formatINR } from "../../utils/currency";

function BookingsHistory() {
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const [loading, setLoading] = useState(true);

  // 🔥 Fetch Bookings + Vehicles + Users
  useEffect(() => {
    const fetchBookings = async () => {
      setLoading(true);

      // 1️⃣ Fetch Bookings
      const { data: bookingsData, error: bookingError } = await supabase
        .from("Bookings")
        .select("*")
        .order("created_at", { ascending: false });

      if (bookingError) {
        console.error(bookingError);
        setLoading(false);
        return;
      }

      // 2️⃣ Fetch Vehicles
      const { data: vehicleData, error: vehicleError } = await supabase
        .from("Vehicles")
        .select("*");

      if (vehicleError) {
        console.error(vehicleError);
      }

      setVehicles(vehicleData || []);
      setBookings(bookingsData || []);
      setLoading(false);
    };

    fetchBookings();
  }, []);

  // Merge vehicle safely (no null error)
  const mergedBookings = bookings.map((booking) => ({
    ...booking,
    vehicle: vehicles.find((v) => v.id == booking.vehicle_id) || null,
  }));

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";

    return new Date(timestamp).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  return (
    <>
      <div className="container" style={{ padding: "20px", marginTop: "50px" }}>
        <h5 className="heading-title mt-4">Bookings</h5>

        {mergedBookings &&
          mergedBookings.map((v) => (
            <>
              <div className="card border-0 shadow-sm mb-3 p-0 rounded-3 overflow-hidden">
                {/* STATUS STRIP */}
                <div
                  style={{
                    height: "6px",
                    backgroundColor:
                      v.status == 0
                        ? "#ffc107"
                        : v.status == 3
                          ? "#0d6efd"
                          : v.status == 4
                            ? "#198754"
                            : v.status == 6
                              ? "#dc3545"
                              : "#adb5bd",
                  }}
                />

                <div className="p-3">
                  {/* TOP SECTION */}

                  <div className="uber-locations mb-1">
                    <div className="location-row">
                      <div className="dot pickup-dot"></div>
                      <div className="location-text">
                        <span className="label">Pickup</span>
                        <span className="value">{v.pickup}</span>
                      </div>
                    </div>

                    <div className="short-vertical-line"></div>

                    <div className="location-row">
                      <div className="square drop-square"></div>
                      <div className="location-text">
                        <span className="label">Drop</span>
                        <span className="value">{v.drop}</span>
                      </div>
                    </div>
                  </div>

                  <small className="text-muted mt-1 mb-0">
                    <span style={{ fontWeight: "400" }}>Booked at:</span>{" "}
                    {formatTimestamp(v.created_at)} • {v.booking_type}
                  </small>

                  <div
                    style={{ position: "absolute", top: "10px", right: "5px" }}
                    className={`p-2 badge ${
                      v.isScheduled ? "bg-dark" : "bg-warning text-dark"
                    }`}
                  >
                    {v.isScheduled ? "Scheduled" : "Instant"}
                  </div>

                  <hr className="mb-2 mt-1" />

                  {/* MIDDLE SECTION */}
                  <div className="row">
                    {/* CUSTOMER */}
                    <div className="col-7">
                      <p className="mb-0 fw-semibold">{v.customer_name}</p>
                      <p className="text-muted mb-0">
                        {v.passengers} Passenger(s)
                      </p>
                    </div>

                    {/* VEHICLE */}
                    <div className="col-5">
                      <p className="fw-semibold mb-0">
                        {v.vehicle?.name || "Vehicle Not Assigned"}
                      </p>
                      <p className="text-muted mb-0">{v.veh_number || "N/A"}</p>
                    </div>
                  </div>

                  <hr className="mb-2 mt-1" />

                  {/* ACTIONS */}
                  <div className="d-flex justify-content-between gap-2 align-items-center">
                    {/* PRICE */}

                    {v.km_price && (
                      <small className="text-muted mb-0">
                        ₹{v.km_price}/km
                      </small>
                    )}

                    <h6 className="mb-0 fw-bold">
                      {formatINR(v.total_price) || 0}
                    </h6>

                    <button
                      className="btn btn-outline-dark btn-sm"
                      data-bs-toggle="modal"
                      data-bs-target="#bookingDetailsModal"
                      onClick={() => setSelectedBooking(v)}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            </>
          ))}
        {mergedBookings.length == 0 ? (
          <p className="text-center text-gray">No Bookings</p>
        ) : null}
      </div>

      {/* BOOKING DETAILS MODAL */}
      <div className="modal fade" id="bookingDetailsModal" tabIndex="-1">
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content rounded-4 border-0 shadow">
            <div className="modal-header border-0">
              <h5 className="modal-title">Booking Details</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              ></button>
            </div>

            <div className="modal-body">
              {selectedBooking && (
                <>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <strong>Customer:</strong>
                      <div>{selectedBooking.customer_name}</div>
                    </div>

                    <div className="col-md-6">
                      <strong>Passengers:</strong>
                      <div>{selectedBooking.passengers}</div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <strong>Pickup:</strong>
                    <div>{selectedBooking.pickup}</div>
                  </div>

                  <div className="mb-3">
                    <strong>Drop:</strong>
                    <div>{selectedBooking.drop}</div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <strong>Booking Type:</strong>
                      <div>{selectedBooking.booking_type}</div>
                    </div>

                    <div className="col-md-6">
                      <strong>Status:</strong>
                      <div>{selectedBooking.status}</div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <strong>Vehicle:</strong>
                      <div>
                        {selectedBooking.vehicle?.name || "Not Assigned"}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <strong>Vehicle Number:</strong>
                      <div>{selectedBooking.veh_number || "N/A"}</div>
                    </div>
                  </div>

                  <hr />

                  <div>
                    <strong>Booked At:</strong>
                    <div>{formatTimestamp(selectedBooking.created_at)}</div>
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer border-0">
              <button className="btn btn-secondary" data-bs-dismiss="modal">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default BookingsHistory;
